import type { GridApi } from "ag-grid-community"
import { useCallback, useEffect, useRef, type RefObject } from "react"

const HEADER_BUTTON_SELECTOR =
  ".ag-header-cell-menu-button, .ag-header-cell-filter-button, .ag-floating-filter-button"
const HEADER_CELL_SELECTOR = ".ag-header-cell, .ag-header-group-cell"

/**
 * `tooltipInteraction` (DataTableV2) turns off AG Grid's hide-on-mousedown, so a header tooltip
 * stays open, or opens again, on top of the column menu or filter popup its own buttons open
 * (SUP-478). Hides header tooltips while the pointer is on one of those buttons or a column menu
 * is open.
 */
export function useHeaderTooltipGuard(containerRef: RefObject<HTMLElement | null>) {
  const headerTooltipRef = useRef<HTMLElement | null>(null)
  const pointerOnButtonRef = useRef(false)
  const menuOpenRef = useRef(false)

  const hideHeaderTooltip = useCallback(() => {
    if (headerTooltipRef.current) headerTooltipRef.current.style.display = "none"
  }, [])

  useEffect(() => {
    const container = containerRef.current
    if (!container) return
    const onPointerOver = (event: PointerEvent) => {
      pointerOnButtonRef.current =
        event.target instanceof Element && event.target.closest(HEADER_BUTTON_SELECTOR) !== null
      if (pointerOnButtonRef.current) hideHeaderTooltip()
    }
    container.addEventListener("pointerover", onPointerOver)
    return () => container.removeEventListener("pointerover", onPointerOver)
  }, [containerRef, hideHeaderTooltip])

  return useCallback(
    (api: GridApi) => {
      api.addEventListener("tooltipShow", (event) => {
        if (!event.parentGui.closest(HEADER_CELL_SELECTOR)) return
        headerTooltipRef.current = event.tooltipGui
        if (menuOpenRef.current || pointerOnButtonRef.current) hideHeaderTooltip()
      })
      api.addEventListener("tooltipHide", (event) => {
        if (event.parentGui.closest(HEADER_CELL_SELECTOR)) headerTooltipRef.current = null
      })
      api.addEventListener("columnMenuVisibleChanged", (event) => {
        menuOpenRef.current = event.visible
        if (event.visible) hideHeaderTooltip()
      })
      api.addEventListener("filterOpened", hideHeaderTooltip)
    },
    [hideHeaderTooltip],
  )
}
